/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Flex,
  Heading,
  Divider,
  Switch,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Link,
  VisuallyHidden,
} from '@chakra-ui/react';
import axios from 'axios';
import { IMerchantSettings } from '@olo-web/types/merchantTheme.interface';
import { useMerchantTheme } from '@domain/merchants/queries/useMerchantTheme';
import { useMerchantGroup } from '@domain/merchants/queries';
import { createLocationString } from '@domain/merchants/functions/createLocationString';
import { useCustomThemeDraftDispatch, useCustomThemeDraftState } from '@olo-web/client-state';
import { useError, useToast } from '@olo-web/utils/common/hooks';
import { useUpdateMerchantTheme } from '@domain/merchants/mutations/useUpdateMerchantTheme';

const DASHBOARD_ADVANCED_SETTINGS_URL =
  'https://dashboard.spoton.com/online-ordering/advanced-settings';
const GOTO_MANAGED_NOTICE_ID = 'goto-managed-in-dashboard-notice';

export const MerchantSettings = () => {
  const { data: theme } = useMerchantTheme();
  const { data: group, isLoading: groupLoading } = useMerchantGroup();
  const { settings } = useCustomThemeDraftState();
  const themeDispatch = useCustomThemeDraftDispatch();
  const callError = useError();
  const { notify } = useToast();
  const [isAppEnabledLocations, setIsAppEnabledLocations] = useState<string[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const { mutate: updateTheme, isLoading: isUpdatingTheme } = useUpdateMerchantTheme({
    onSuccess: () => {
      notify({
        status: 'success',
        title: 'Settings updated',
        description: 'Your Picked For You settings have been saved',
        variant: 'left-accent',
        position: 'bottom-left',
        duration: 2000,
      });
    },
    onError: (error) => callError({ error, toastError: true, title: 'Unable to save settings' }),
  });

  const updateDraftSettings = (data: IMerchantSettings) => {
    themeDispatch({
      type: 'UPDATE_MERCHANT_SETTINGS',
      payload: data,
    });
  };

  const handleRecommendationsToggle = (isChecked: boolean) => {
    if (!theme?.id) return;

    updateDraftSettings({
      ...settings,
      areRecommendationsEnabled: isChecked,
    });

    updateTheme({
      theme: {
        merchantIds: theme.merchantIds,
        settings: {
          ...settings,
          areRecommendationsEnabled: isChecked,
        },
      },
    });
  };

  useEffect(() => {
    const fetchInitialStatus = async () => {
      if (!group?.merchants?.length) {
        return;
      }

      try {
        const enabledLocations = await Promise.all(
          group.merchants.map(async (merchant) => {
            const { data } = await axios.get(
              `/api/digital-ordering/locations/${merchant.merchantId}`
            );
            return data.isAppEnabled ? merchant.merchantId : null;
          })
        );

        setIsAppEnabledLocations(enabledLocations.filter(Boolean));
      } catch (error) {
        callError({ error, toastError: true });
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialStatus();
  }, [group?.merchants]);

  const isRecommendationsDataReady = Boolean(theme?.id);

  useEffect(() => {
    if (!settings && theme?.settings) {
      updateDraftSettings(theme.settings);
    }
    // intentionally leaving `updateDraftSettings` out of the dependency array
  }, [theme?.settings, settings]);

  return (
    <VStack w="100%" spacing={10} align="flex-start">
      <Text>Configure merchant settings</Text>

      <Box w="100%" p={5} borderWidth="1px" borderRadius="md" bg="gray.50">
        <VStack align="flex-start" spacing={4} w="100%">
          <Flex w="100%" justifyContent="space-between" alignItems="center">
            <Heading as="h3" size="md" color="primary.600">
              Picked For You
            </Heading>
          </Flex>
          <Divider borderColor="gray.300" />
          <Text fontSize="sm" color="gray.600">
            Enable personalized recommendations
          </Text>

          <Flex
            w="100%"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            borderRadius="md"
            _hover={{ bg: 'gray.100' }}
          >
            <Flex flexDir="column">
              <Text textStyle="h3">Enable Picked For You</Text>
            </Flex>
            <Switch
              size="lg"
              colorScheme="primary"
              isChecked={settings?.areRecommendationsEnabled ?? false}
              onChange={(e) => handleRecommendationsToggle(e.target.checked)}
              isDisabled={isUpdatingTheme || !isRecommendationsDataReady}
            />
          </Flex>
        </VStack>
      </Box>

      <Box w="100%" p={5} borderWidth="1px" borderRadius="md" bg="gray.50">
        <VStack align="flex-start" spacing={4} w="100%">
          <Flex w="100%" justifyContent="space-between" alignItems="center">
            <Heading as="h3" size="md" color="primary.600">
              GoTo Settings
            </Heading>
          </Flex>
          <Alert status="info" borderRadius="md" alignItems="flex-start">
            <AlertIcon />
            <Box>
              <AlertTitle id={GOTO_MANAGED_NOTICE_ID}>
                GoTo visibility is now managed in the SpotOn Dashboard
              </AlertTitle>
              <AlertDescription>
                To show or hide a location in the GoTo app, go to{' '}
                <Link href={DASHBOARD_ADVANCED_SETTINGS_URL} isExternal textDecoration="underline">
                  Online Ordering &gt; Advanced Settings
                  <VisuallyHidden> (opens in a new tab)</VisuallyHidden>
                </Link>
                .
              </AlertDescription>
            </Box>
          </Alert>
          <Divider borderColor="gray.300" />
          <Text fontSize="sm" color="gray.600">
            Locations visible in the GoTo app
          </Text>

          <VStack w="100%" spacing={5} align="flex-start" mt={2}>
            {initialLoading ? (
              <Text>Loading locations...</Text>
            ) : (
              group?.merchants?.map((merchant) => (
                <Flex
                  key={merchant.merchantId}
                  w="100%"
                  justifyContent="space-between"
                  alignItems="center"
                  p={2}
                  borderRadius="md"
                  _hover={{ bg: 'gray.100' }}
                >
                  <Flex flexDir="column">
                    <Text textStyle="h3">{merchant.name}</Text>
                    <Text textStyle="subtitle1" fontWeight="400" color="gray.600">
                      {createLocationString(merchant)}
                    </Text>
                  </Flex>
                  <Switch
                    size="lg"
                    colorScheme="primary"
                    aria-label={`GoTo visibility for ${merchant.name}`}
                    aria-describedby={GOTO_MANAGED_NOTICE_ID}
                    isChecked={isAppEnabledLocations.includes(merchant.merchantId)}
                    isDisabled
                  />
                </Flex>
              ))
            )}
          </VStack>
        </VStack>
      </Box>
    </VStack>
  );
};
